import { useRef, useState, useEffect, useMemo, memo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

const _vecA = new THREE.Vector3();
const _vecB = new THREE.Vector3();

interface ArrowProps {
    id: string;
    active: boolean;
    position: THREE.Vector3;
    targetPoint: THREE.Vector3;
    speed: number;
    startTimeStamp: number;
}

export const Arrow = memo(({ id, active, position, targetPoint, speed, startTimeStamp }: ArrowProps) => {
    const groupRef = useRef<THREE.Group>(null);
    const [stuck, setStuck] = useState(false);
    const deactivateProjectile = useStore((state) => state.deactivateProjectile);
    const spawnHit = useStore((state) => state.spawnHit);

    // Calculate total distance and duration
    const { duration } = useMemo(() => {
        const dist = position.distanceTo(targetPoint);
        return { duration: dist / speed };
    }, [position, targetPoint, speed]);

    const startTime = useRef(startTimeStamp);
    useEffect(() => {
        if (active) {
            startTime.current = startTimeStamp;
            setStuck(false);
        }
    }, [active, startTimeStamp, id]);

    useFrame(() => {
        if (!active || stuck || !groupRef.current) return;

        const elapsed = (performance.now() - startTime.current) / 1000;
        const progress = Math.min(elapsed / duration, 1);

        // Update position (Reusing vectors to avoid GC)
        _vecA.lerpVectors(position, targetPoint, progress);
        groupRef.current.position.copy(_vecA);

        // Update rotation to look at target
        groupRef.current.lookAt(targetPoint);
        groupRef.current.rotateX(Math.PI / 2); // Adjust for mesh orientation

        if (progress >= 1) {
            setStuck(true);
            spawnHit([targetPoint.x, targetPoint.y, targetPoint.z], "#ffffff");

            // Deactivate and recycle after sticking
            const timer = setTimeout(() => {
                deactivateProjectile(id);
            }, 3000);
            return () => clearTimeout(timer);
        }
    });

    return (
        <group ref={groupRef} position={[position.x, position.y, position.z]} visible={active}>
            <group rotation={[0, 0, 0]}> {/* Visual mesh adjustment */}
                <mesh castShadow receiveShadow>
                    <cylinderGeometry args={[0.02, 0.02, 0.8, 8]} />
                    <meshStandardMaterial color="brown" />
                </mesh>
                <mesh position={[0, 0.4, 0]}>
                    <coneGeometry args={[0.04, 0.1, 8]} />
                    <meshStandardMaterial color="#ddd" metalness={0.8} roughness={0.2} />
                </mesh>
                <mesh position={[0, -0.35, 0]}>
                    <boxGeometry args={[0.1, 0.02, 0.1]} />
                    <meshStandardMaterial color="white" />
                </mesh>
                {active && !stuck && (
                    <Sparkles count={10} scale={0.5} size={2} speed={0.2} opacity={0.3} color="#ffffff" position={[0, -0.5, 0]} />
                )}
            </group>
        </group>
    );
});
